import React, { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as RefreshIcon } from '../../../../assets/icons/dashboard/rotate-left.svg';
import { ReactComponent as LoaderIcon } from '../../../../assets/icons/loader.svg';
// import { ReactComponent as NavigateArrow } from '../../../assets/icons/dashboard/src/assets/icons/dashboard/navigate-arrow.svg';
import currentTimeZone from '../../../../config/timeZone';
// import { useAppSelector } from '../../../redux/hooks';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../../shared-components/button/Button';
// import ToggleWithIcons from '../../../shared-components/toggle-with-icons/ToggleWithIcons';
import NoDataAvailable from '../../../../shared-components/no-data-available/NoDataAvailable';
import { TooltipIcon } from '../../../../shared-components/tooltip/TooltipIcon';
import { getBucketFromRange } from '../../../../shared-functions/ChartFunctions';
import { getRelativeTime } from '../../../../shared-functions/DateTime';
import dashboard from '../../../../utils/api/dashboard';

interface ChartDataProperties {
  label: string;
  data: number[];
}

interface DeviceUtilizationChartProperties {
  startDate: string;
  endDate: string;
  filters?: {
    facilityIds: string[];
    stationIds: string[];
    deviceIds: string[];
    type: string[];
  };
}

/**
 * Device utilization report chart
 * @param {any} param0 - The param0
 * @param {any} param0.startDate - The startDate prop
 * @param {any} param0.endDate - The endDate prop
 * @param {any} param0.filters - The filters prop
 * @returns {any} The result
 */
function DeviceUtilizationChart({ startDate, endDate, filters }: DeviceUtilizationChartProperties) {
  const { t, i18n } = useTranslation('dashboard');
  const navigate = useNavigate();
  // const { deviceUtilization } = useAppSelector((state) => state.chargerStatus);
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [lastUpdatedTime, setLastUpdatedTime] = useState<string>('');
  const [chartOptions, setChartOptions] = useState<ApexCharts.ApexOptions>({
    chart: { type: 'heatmap', toolbar: { show: false } },
    xaxis: {
      categories: Array.from({ length: 24 }, (_, index) => `${index + 1} hr`),
      labels: {
        rotate: 0,
        style: {
          fontSize: '12px',
        },
      },
    },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.5,
        radius: 6,
        useFillColorAsStroke: true,
        colorScale: { ranges: [] },
      },
    },
    dataLabels: { enabled: false },
    legend: {
      show: false, // Hide the legend since we're using a custom one
      position: 'bottom', // If you want to move the legend to the bottom as well
    },
    tooltip: {
      /**
       * Custom tooltip renderer for heatmap cells.
       * @param {any} param0 - The chart's series data.
       * @param {any} param0.series - The series prop
       * @param {any} param0.seriesIndex - The seriesIndex prop
       * @param {any} param0.dataPointIndex - The dataPointIndex prop
       * @returns {any} HTML string to be rendered inside the tooltip.
       */
      custom({ series, seriesIndex, dataPointIndex }): string {
        const value = series[seriesIndex][dataPointIndex];

        return `
          <div style="padding: 8px; font-size: 13px;">
            ${((value || 0) / 1000).toFixed(2)} kW
          </div>
        `;
      },
    },
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const activeRangeReference = React.useRef<string>('');
  // const [chartToggle, setChartToggle] = useState([
  //   {
  //     label: t('personal'),
  //     key: 'personal',
  //     isActive: true,
  //   },
  //   {
  //     label: t('commercial'),
  //     key: 'commercial',
  //     isActive: false,
  //   },
  // ]);
  // const [activeChargerType, setActiveChargerType] = useState<'personal' | 'commercial'>('personal');

  /**
   * Calculate median value and update chart options
   * @param {any} chartData - The chartData
   */
  const findMedianValueFromData = (chartData: ChartDataProperties[]) => {
    const allValues = chartData
      .flatMap((month) => month.data)
      .filter((value) => typeof value === 'number' && !Number.isNaN(value) && value !== 0);

    if (allValues.length === 0) {
      return;
    }

    const sorted = [...allValues].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    const median = sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
    const roundedMedian = Math.round(median);

    // Update chart options with new color scale
    setChartOptions((previous) => ({
      ...previous,
      plotOptions: {
        heatmap: {
          ...previous.plotOptions?.heatmap,
          colorScale: {
            ranges: [
              { from: 0, to: roundedMedian, name: t('low'), color: '#2E9E3A' },
              {
                from: roundedMedian,
                to: Math.max(...allValues) || roundedMedian * 2,
                name: t('high'),
                color: '#F04438',
              },
            ],
          },
        },
      },
    }));
  };

  /**
   * Fetch power consumed data based on the selected date range
   * @param {any} rangeStartDate - The range start date
   * @param {any} rangeEndDate - The range end date
   * @param {any} hardRefresh - The hardRefresh
   */
  const fetchPowerConsumedReport = async (
    rangeStartDate: string,
    rangeEndDate: string,
    hardRefresh = false,
  ) => {
    const rangeKey = `${rangeStartDate}_${rangeEndDate}`;
    activeRangeReference.current = rangeKey;
    try {
      setIsLoading(true);
      const { facilityIds, stationIds, deviceIds, type } = filters || {};
      const query: any = {
        startDate: rangeStartDate,
        endDate: rangeEndDate,
        timeZone: currentTimeZone,
        hardRefresh,
        type,
      };
      if (facilityIds && facilityIds?.length > 0) {
        query.accountCustomId = facilityIds;
      }
      if (stationIds && stationIds?.length > 0) {
        query.stationIds = stationIds;
      }
      if (deviceIds && deviceIds?.length > 0) {
        query.deviceIds = deviceIds;
      }
      const response = await dashboard.getDeviceUtilizationReport(query);

      // ❌ If user already switched to another range, ignore this response
      if (activeRangeReference.current !== rangeKey) return;

      // ✅ Otherwise, update the chart with the right data
      const updatedTime = response.data?.last_updated_time;
      const { bucket } = getBucketFromRange(rangeStartDate, rangeEndDate);

      if (bucket === 'month') {
        setBarGraphData(response?.data.result || []);
      } else {
        setBarGraphData(
          response?.data?.result.map((eachData: any) => ({
            ...eachData,
            name: eachData?.name?.replace(/Z$/, ''),
          })),
        );
      }
      findMedianValueFromData(response?.data.result || []);
      if (updatedTime) {
        setLastUpdatedTime(updatedTime);
      }
      setIsLoading(false);
      setIsRefreshing(false);
    } catch (error) {
      console.log(error);

      // only stop loading if this request is still the active one
      if (activeRangeReference.current === rangeKey) {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    }
  };

  /**
   * Function to switch the tab
   * @param key
   */
  // const handleToggle = async (
  //   key: string,
  //   range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  //   hardRefresh = false,
  // ) => {
  //   setChartToggle((previousTab) =>
  //     previousTab.map((tab) => ({ ...tab, isActive: tab.key === key })),
  //   );
  //   try {
  //     setIsLoading(true);
  //     const { facilityIds, stationIds, deviceIds, type: filterTypes } = filters || {};

  //     let requestType = key as 'personal' | 'commercial';
  //     if (filterTypes?.includes('personal') && !filterTypes?.includes('commercial')) requestType = 'personal';
  //     else if (!filterTypes?.includes('personal') && filterTypes?.includes('commercial')) requestType = 'commercial';

  //     const response = await dashboard.getDeviceUtilizationReport({
  //       timePeriod: range,
  //       timeZone: currentTimeZone,
  //       hardRefresh,
  //       facilityIds: facilityIds?.join(','),
  //       stationIds: stationIds?.join(','),
  //       deviceIds: deviceIds?.join(','),
  //       type: filterTypes?.length === 2 ? filterTypes : [requestType],
  //     });

  //     const resultData = response?.data?.result || [];

  //     if (range === 'last12months') {
  //       setBarGraphData(resultData);
  //     } else {
  //       setBarGraphData(
  //         resultData.map((eachData: any) => ({
  //           ...eachData,
  //           name: eachData?.name?.replace(/Z$/, ''),
  //         })),
  //       );
  //     }

  //     findMedianValueFromData(resultData);
  //     if (response.data?.last_updated_time) {
  //       setLastUpdatedTime(response.data.last_updated_time);
  //     }
  //     setIsLoading(false);
  //   } catch (error) {
  //     console.error(error);
  //     setIsLoading(false);
  //   }
  // };

  useEffect(() => {
    // const activeType = chartToggle.find((iteam) => iteam.isActive)?.key as 'personal' | 'commercial';
    // setActiveChargerType(activeType);
    fetchPowerConsumedReport(startDate, endDate, true);
  }, [startDate, endDate, filters]);

  // useEffect(() => {
  //   setChartToggle((previousTab) =>
  //     previousTab.map((tab) => ({ ...tab, isActive: tab.key === deviceUtilization })),
  //   );

  //   if (deviceUtilization) {
  //     setActiveChargerType(deviceUtilization as 'personal' | 'commercial');
  //     handleToggle(deviceUtilization, dateDuration);
  //   }
  // }, [deviceUtilization]);

  const isEmpty =
    barGraphData.length === 0 ||
    barGraphData.every((item) => item.data.every((value) => value === 0));

  return (
    <div className="dashboard-device-utilization-chart-wrap" dir={i18n.dir()}>
      <div className="dashboard-device-utilization-chart-header">
        <div className="header-title">
          <div className="title-wrapper">
            <h6>
              {t('utilization')} ({t('device')})
            </h6>
            <TooltipIcon text="Utilization charger chart" infoColor="info" />
          </div>
          <div className="refresh">
            <div className="refresh-icon">
              {isRefreshing ? (
                <div className="loading">
                  <LoaderIcon style={{ width: '20px', height: '20px' }} />
                </div>
              ) : (
                <MyButton
                  label=""
                  buttonType="none"
                  onClickFunc={() => {
                    fetchPowerConsumedReport(
                      startDate,
                      endDate,
                      true, // hard refresh on button click
                    );
                    setIsRefreshing(true);
                  }}
                  svgBackIcon={<RefreshIcon style={{ width: '20px', height: '20px' }} />}
                />
              )}
            </div>
            <p className="updated-time">
              {t('lastUpdatedTime')} : {lastUpdatedTime ? getRelativeTime(lastUpdatedTime, t) : ''}
            </p>
          </div>
          <span
            className="header-arrow"
            role="presentation"
            onClick={() => navigate('/ev-admin/operations/chargers')}>
            {/* <NavigateArrow /> */}
          </span>
        </div>
      </div>
      <div className="dashboard-device-utilization-chart-container">
        {isLoading ? (
          <center className="device-utilization-loader">
            <BatteryLoader />
          </center>
        ) : null}

        {!isLoading && isEmpty && <NoDataAvailable />}

        {!isLoading && !isEmpty && (
          <Chart options={chartOptions} series={barGraphData} type="heatmap" height={350} />
        )}
      </div>
      {/* <div className="device-utilization-toggle">
        <ToggleWithIcons
          leftMenu={{ ...chartToggle[0], label: t('personal') }}
          rightMenu={{ ...chartToggle[1], label: t('commercial') }}
          handleClickToggle={(key: string) => {
            setActiveChargerType(key as 'personal' | 'commercial');
            handleToggle(key, dateDuration);
          }}
        />
      </div> */}
    </div>
  );
}

DeviceUtilizationChart.defaultProps = {
  filters: {
    facilityIds: [],
    stationIds: [],
    deviceIds: [],
    type: ['commercial'],
  },
};

export default DeviceUtilizationChart;
